import {useParams} from "react-router-dom";
import {useSnackbar} from "../../context/SnackbarContext/SnackbarContext";
import React, {useEffect, useState} from "react";
import {
    getSalesByDoctorService,
    getSalesByLocationService, getSalesByProductService,
    getSalesOverTimeService
} from "../../services/dashboardService";
import moment from "moment";
import {listDoctorsService} from "../../services/doctorService";
import {capitalizeFirstLetter} from "../../helpers/helpers";
import {listLocationsService} from "../../services/locationService";
import {useSelector} from "react-redux";

const useDashboard = () => {

    const authState = useSelector((state) => state.authReducer);

    const { showSnackbar } = useSnackbar();

    const [isLoading, setIsLoading] = useState(true);

    const [doctors, setDoctors] = useState(null);
    const [locations, setLocations] = useState(null);

    const endDate = moment().format('YYYY-MM-DD');
    const startDate = moment().subtract(7, 'days').format('YYYY-MM-DD');

    const [dateRange, setDateRange] = useState([startDate, endDate]);
    const [displayRange, setDisplayRange] = useState('1 Nov, 2024 - 20 Dec, 2024');
    const [doctorId, setDoctorId] = useState(-1);
    const [locationId, setLocationId] = useState(authState.locationId !== null ? authState.locationId : -1);

    const [salesByLocation, setSalesByLocation] = useState(null);
    const [salesByDoctor, setSalesByDoctor] = useState(null);
    const [salesByProduct, setSalesByProduct] = useState(null);
    const [feesByDoctor, setFeesByDoctor] = useState(null);

    const [salesOverTime, setSalesOverTime] = useState(null);
    const [feesOverTime, setFeesOverTime] = useState(null);
    const [businessFeesOverTime, setBusinessFeesOverTime] = useState(null);
    const [salesCountOverTime, setSalesCountOverTime] = useState(null);
    const [salesAverageOverTime, setSalesAverageOverTime] = useState(null);

    useEffect(() => {

        const init = async () => {
            setIsLoading(true);
            await loadDoctors();
            await loadLocations();
            await loadCharts();
            setIsLoading(false);
        };

        init();
    }, []);

    useEffect(() => {
        loadCharts()
    }, [dateRange, doctorId, locationId]);

    const loadCharts = async () => {
        setIsLoading(true);
        await loadSalesByLocation();
        await loadSalesByDoctor();
        await loadSalesByProduct();
        await loadSalesOverTime();
        setIsLoading(false);
    };

    const loadDoctors = async () => {

        try {

            const response = await listDoctorsService({
                sort: 'name',
                direction: 'asc',
                active: true
            });

            if(response.data.data.length > 0)
                setDoctors([{id: -1, label: "Selecciona un doctor"}, ...response.data.data.map(doctor => ({id: doctor.id, label: capitalizeFirstLetter(doctor.name)}))]);
            else
                setDoctors([{id: -1, label: "Selecciona un doctor"}]);

        } catch (error) {

            // setSnackbarConfig({
            //     message: "Ocurrió un error cargando los doctores",
            //     icon: 'cancel',
            //     title: 'Doctores',
            //     color: 'warning'
            // });
            //
            // setShow(true);

        }
    };

    const loadLocations = async () => {

        try {

            const response = await listLocationsService({
                sort: 'name',
                direction: 'asc',
                active: true
            });

            if(response.data.data.length > 0)
                setLocations([{id: -1, label: "Selecciona una sucursal"}, ...response.data.data.map(location => ({id: location.id, label: location.display_name}))]);
            else
                setLocations([{id: -1, label: "Selecciona una sucursal"}]);

        } catch (error) {

            // setSnackbarConfig({
            //     message: "Ocurrió un error cargando las sucursales",
            //     icon: 'cancel',
            //     title: 'Sucursales',
            //     color: 'warning'
            // });
            //
            // setShow(true);

        }
    };

    const loadSalesByLocation = async () => {

        try {

            const postData = {
                start_date: dateRange[0],
                end_date: dateRange[1],
            }

            if(doctorId > 0){
                postData.doctor_id = doctorId;
            }

            const response = await getSalesByLocationService(postData);

            const labels = response.data.map(item => item.name);
            const data = response.data.map(item => parseFloat(item.total_sales));

            setSalesByLocation({
                labels: labels,
                datasets: [
                    {
                        label: 'Ventas',
                        data: data,
                        color: 'secondary'
                    }
                ]
            });

        } catch (error) {

            showSnackbar('Dashboard', 'Ocurrió un error cargando las ventas por ubicación', 'cancel', 'warning');
        }

    };

    const loadSalesByProduct = async () => {

        try {

            const postData = {
                start_date: dateRange[0],
                end_date: dateRange[1],
            }

            if(doctorId > 0){
                postData.doctor_id = doctorId;
            }

            if(locationId > 0){
                postData.location_id = locationId;
            }

            const response = await getSalesByProductService(postData);

            const labels = response.data.map(item => item.name);
            const data = response.data.map(item => parseFloat(item.total_sales));

            setSalesByProduct({
                labels: labels,
                datasets: [
                    {
                        label: 'Ventas',
                        data: data,
                        color: 'secondary'
                    }
                ]
            });

        } catch (error) {

            showSnackbar('Dashboard', 'Ocurrió un error cargando las ventas por producto', 'cancel', 'warning');
        }

    };

    const loadSalesByDoctor = async () => {

        try {

            const postData = {
                start_date: dateRange[0],
                end_date: dateRange[1],
            }

            if(locationId > 0){
                postData.location_id = locationId;
            }

            const response = await getSalesByDoctorService(postData);

            const data = response.data;

            setupSalesByDoctor(data);
            setupFeesByDoctor(data);

        } catch (error) {

            showSnackbar('Dashboard', 'Ocurrió un error cargando las ventas por doctor', 'cancel', 'warning');
        }

    };

    const loadSalesOverTime = async () => {

        try {

            const postData = {
                start_date: dateRange[0],
                end_date: dateRange[1],
            }

            if(doctorId > 0){
                postData.doctor_id = doctorId;
            }

            if(locationId > 0){
                postData.location_id = locationId;
            }

            const response = await getSalesOverTimeService(postData);

            const data = response.data;

            setupSalesTotalChart(data, findTimescale(dateRange[0], dateRange[1]));
            setupSalesCountChart(data, findTimescale(dateRange[0], dateRange[1]));
            setupSalesAverageChart(data, findTimescale(dateRange[0], dateRange[1]));
            setupFeesTotalChart(data, findTimescale(dateRange[0], dateRange[1]));
            setupBusinessFeesTotalChart(data, findTimescale(dateRange[0], dateRange[1]));

        } catch (error) {

            showSnackbar('Dashboard', 'Ocurrió un error cargando las ventas por ubicación', 'cancel', 'warning');
        }

    };

    const setupSalesTotalChart = (data, timescale) => {

        const formatMap = {
            'day': 'DD MMM YYYY',
            'week': 'W Y',
            'month': 'MMM YYYY',
            'year': 'YYYY'
        };

        const unitsCount = moment(dateRange[1]).diff(dateRange[0], timescale);

        const formatString = formatMap[timescale];

        const periods = {};

        for (let i = 0; i <= unitsCount; i++) {
            const key = moment(dateRange[0]).add(i, timescale).format(formatString);
            periods[key] = 0;
        }

        let total = 0;

        data.forEach(item => {
            let periodKey;

            if(timescale === 'week') {
                periodKey = moment(item.period, 'GGGG-WW').format(formatString);
            } else {
                periodKey = moment(item.period).format(formatString);
            }

            if (periods.hasOwnProperty(periodKey)) {
                periods[periodKey] += parseFloat(item.total);
            }
            total += parseFloat(item.total);
        });

        setSalesOverTime({
            total,
            labels: Object.keys(periods),
            datasets: [{
                label: 'Ventas',
                data: Object.values(periods),
                color: 'secondary'
            }]
        });
    };

    const setupFeesTotalChart = (data, timescale) => {

        const formatMap = {
            'day': 'DD MMM YYYY',
            'week': 'W Y',
            'month': 'MMM YYYY',
            'year': 'YYYY'
        };

        const unitsCount = moment(dateRange[1]).diff(dateRange[0], timescale);

        const formatString = formatMap[timescale];

        const periods = {};

        for (let i = 0; i <= unitsCount; i++) {
            const key = moment(dateRange[0]).add(i, timescale).format(formatString);
            periods[key] = 0;
        }

        let total = 0;

        data.forEach(item => {
            let periodKey;

            if(timescale === 'week') {
                periodKey = moment(item.period, 'GGGG-WW').format(formatString);
            } else {
                periodKey = moment(item.period).format(formatString);
            }

            if (periods.hasOwnProperty(periodKey)) {
                periods[periodKey] += parseFloat(item.doctor_fee_total);
            }
            total += parseFloat(item.doctor_fee_total);
        });

        setFeesOverTime({
            total,
            labels: Object.keys(periods),
            datasets: [{
                label: 'Comisión',
                data: Object.values(periods),
                color: 'secondary'
            }]
        });
    };

    const setupBusinessFeesTotalChart = (data, timescale) => {

        const formatMap = {
            'day': 'DD MMM YYYY',
            'week': 'W Y',
            'month': 'MMM YYYY',
            'year': 'YYYY'
        };

        const unitsCount = moment(dateRange[1]).diff(dateRange[0], timescale);

        const formatString = formatMap[timescale];

        const periods = {};

        for (let i = 0; i <= unitsCount; i++) {
            const key = moment(dateRange[0]).add(i, timescale).format(formatString);
            periods[key] = 0;
        }

        let total = 0;

        data.forEach(item => {
            let periodKey;

            if(timescale === 'week') {
                periodKey = moment(item.period, 'GGGG-WW').format(formatString);
            } else {
                periodKey = moment(item.period).format(formatString);
            }

            if (periods.hasOwnProperty(periodKey)) {
                periods[periodKey] += parseFloat(item.business_fee_total);
            }
            total += parseFloat(item.business_fee_total);
        });

        setBusinessFeesOverTime({
            total,
            labels: Object.keys(periods),
            datasets: [{
                label: 'Ganancias',
                data: Object.values(periods),
                color: 'secondary'
            }]
        });
    };

    const setupSalesCountChart = (data, timescale) => {

        const formatMap = {
            'day': 'DD MMM YYYY',
            'week': 'W Y',
            'month': 'MMM YYYY',
            'year': 'YYYY'
        };

        const unitsCount = moment(dateRange[1]).diff(dateRange[0], timescale);

        const formatString = formatMap[timescale];

        const periods = {};

        for (let i = 0; i <= unitsCount; i++) {
            const key = moment(dateRange[0]).add(i, timescale).format(formatString);
            periods[key] = 0;
        }

        let total = 0;

        data.forEach(item => {
            let periodKey;

            if(timescale === 'week') {
                periodKey = moment(item.period, 'GGGG-WW').format(formatString);
            } else {
                periodKey = moment(item.period).format(formatString);
            }

            if (periods.hasOwnProperty(periodKey)) {
                periods[periodKey] += parseFloat(item.sales_count);
            }
            total += parseFloat(item.sales_count);
        });

        setSalesCountOverTime({
            total: total,
            labels: Object.keys(periods),
            datasets: [{
                label: 'Ventas',
                data: Object.values(periods),
                color: 'secondary'
            }]
        });
    };

    const setupSalesAverageChart = (data, timescale) => {
        const formatMap = {
            'day': 'DD MMM YYYY',
            'week': 'W Y',
            'month': 'MMM YYYY',
            'year': 'YYYY'
        };

        const unitsCount = moment(dateRange[1]).diff(dateRange[0], timescale);

        const formatString = formatMap[timescale];

        const periods = {};

        for (let i = 0; i <= unitsCount; i++) {
            const key = moment(dateRange[0]).add(i, timescale).format(formatString);
            periods[key] = 0;
        }

        let average = 0;

        data.forEach(item => {
            let periodKey;

            if(timescale === 'week') {
                periodKey = moment(item.period, 'GGGG-WW').format(formatString);
            } else {
                periodKey = moment(item.period).format(formatString);
            }

            if (periods.hasOwnProperty(periodKey)) {
                periods[periodKey] += parseFloat(item.average_total);
            }
            average += parseFloat(item.average_total);
        });

        average = average / data.length;

        setSalesAverageOverTime({
            total: average,
            labels: Object.keys(periods),
            datasets: [{
                label: 'Valor medio',
                data: Object.values(periods),
                color: 'secondary'
            }]
        });
    };

    const setupSalesByDoctor = (data) => {
        const labels = data.map(item => item.name);
        const dataset = data.map(item => parseFloat(item.total_sales));

        setSalesByDoctor({
            labels: labels,
            datasets: [
                {
                    label: 'Ventas',
                    data: dataset,
                    color: 'secondary'
                }
            ]
        });
    }

    const setupFeesByDoctor = (data) => {
        const labels = data.map(item => item.name);
        const dataset = data.map(item => parseFloat(item.doctor_fee_total));

        setFeesByDoctor({
            labels: labels,
            datasets: [
                {
                    label: 'Comisión',
                    data: dataset,
                    color: 'secondary'
                }
            ]
        });
    }

    const findTimescale = (startDate, endDate) => {
        const diffInDays = moment(endDate).diff(startDate, 'days');

        if (diffInDays <= 14) {
            return 'day';
        } else if (diffInDays <= 90) {
            return 'week';
        } else if (diffInDays <= 365) {
            return 'month';
        } else {
            return 'year';
        }
    };

    return {
        doctors,
        locations,
        dateRange,
        displayRange,
        doctorId,
        locationId,
        setDisplayRange,
        setDateRange,
        isLoading,
        salesByLocation,
        salesByDoctor,
        salesByProduct,
        salesOverTime,
        salesCountOverTime,
        salesAverageOverTime,
        feesOverTime,
        feesByDoctor,
        businessFeesOverTime,
        setDoctorId,
        setLocationId,
        authState
    };
};

export default useDashboard;
